// 🎫 Coupon Manager Page (Super Admin Only)
/**
 * 슈퍼 관리자 전용 쿠폰 발행 페이지
 * - 백엔드 middleware/auth.py 의 require_super_admin 화이트리스트와 동기화
 * - 쿠폰 일괄 발행 기능
 * - 생성된 쿠폰 목록 표시 및 CSV 다운로드
 * - 사용자 쿠폰 관리 (검색, 철회)
 *
 * Author: feature/admin-coupon-manager
 * Created: 2025-11-03
 * Updated: 2025-12-09 (사용자 쿠폰 관리 기능 추가)
 * Updated: 2026-05-27 (단일 이메일 → 화이트리스트로 변경)
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/config';
import { getAdminPermissions } from '../../api/adminService';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { couponDateInputValue, couponValidUntilIso } from '../../utils/couponDates';
import './CouponManager.css';

const CouponManager = () => {
  const [user, loading] = useAuthState(auth);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [hasSuperAdminAccess, setHasSuperAdminAccess] = useState(false);
  
  // 폼 상태
  const [formData, setFormData] = useState({
    series_name: '',
    benefit_type: 'months', // 'months' | 'days'
    benefit_months: 3,
    benefit_days: 30,
    count: 1,
    max_uses_per_coupon: 1,
    permanent: true,
    valid_until: '',
    description: '',
    coupon_group: ''
  });
  
  // UI 상태
  const [isCreating, setIsCreating] = useState(false);
  const [createdCoupons, setCreatedCoupons] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // 사용자 쿠폰 관리 상태
  const [searchForm, setSearchForm] = useState({
    email: '',
    coupon_code: ''
  });
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [searchSuccess, setSearchSuccess] = useState(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [isRevoking, setIsRevoking] = useState(false);

  // 단일 쿠폰 생성 상태
  const [singleCouponForm, setSingleCouponForm] = useState({
    code: '',
    name: '',
    description: '',
    benefit_type: 'months', // 'months' | 'days'
    benefit_months: 3,
    benefit_days: 30,
    max_uses: 1,
    permanent: true,
    valid_until: '',
    coupon_group: ''
  });
  const [isCreatingSingle, setIsCreatingSingle] = useState(false);

  // 마스터 쿠폰 목록 상태
  const [masterCoupons, setMasterCoupons] = useState([]);
  const [isLoadingMasterCoupons, setIsLoadingMasterCoupons] = useState(false);
  const [masterCouponsError, setMasterCouponsError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // 쿠폰 수정 모달 상태
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    benefit_days: 30,
    max_uses: 1,
    active: true,
    valid_until: '',
    coupon_group: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // 쿠폰 삭제 모달 상태
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCoupon, setDeletingCoupon] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 탭 상태
  const [createMode, setCreateMode] = useState('bulk'); // 'bulk' | 'single'

  // 일괄 삭제 상태
  const [selectedCoupons, setSelectedCoupons] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // 권한 체크: 실제 차단은 백엔드 require_super_admin 이 수행하고, 프론트는 UX 가드만 담당한다.
  useEffect(() => {
    let cancelled = false;

    const loadPermissions = async () => {
      if (loading) return;
      if (!user) {
        setPermissionLoading(false);
        setHasSuperAdminAccess(false);
        return;
      }

      setPermissionLoading(true);
      try {
        const permissions = await getAdminPermissions();
        if (!cancelled) {
          setHasSuperAdminAccess(Boolean(permissions?.is_super_admin));
        }
      } catch (err) {
        if (!cancelled) {
          setHasSuperAdminAccess(false);
          setError(err.message || '슈퍼 관리자 권한을 확인하지 못했습니다');
        }
      } finally {
        if (!cancelled) setPermissionLoading(false);
      }
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [user, loading]);

  useEffect(() => {
    if (user && !permissionLoading && !hasSuperAdminAccess) {
      setError('이 페이지는 슈퍼 관리자 전용입니다');
    }
  }, [user, permissionLoading, hasSuperAdminAccess]);

  // 입력 핸들러
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // 쿠폰 생성 API 호출
  const handleCreateCoupons = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setError('로그인이 필요합니다');
      return;
    }

    if (!hasSuperAdminAccess) {
      setError('슈퍼 관리자 권한이 필요합니다');
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      setSuccess(null);

      const token = await user.getIdToken(true);
      
      const requestBody = {
        series_name: formData.series_name.toUpperCase(),
        count: parseInt(formData.count),
        max_uses_per_coupon: parseInt(formData.max_uses_per_coupon),
        permanent: formData.permanent,
        valid_until: formData.permanent ? null : couponValidUntilIso(formData.valid_until),
        description: formData.description || undefined,
        coupon_group: (formData.coupon_group || '').trim() || null,
        metadata: {
          created_via: 'admin_panel',
          created_by_email: user.email
        }
      };
      
      // 혜택 기간 설정 (일 단위 또는 개월 단위)
      if (formData.benefit_type === 'days') {
        requestBody.benefit_days = parseInt(formData.benefit_days);
      } else {
        requestBody.benefit_months = parseInt(formData.benefit_months);
      }

      const response = await fetch(`${getApiBaseUrl()}/api/coupons/bulk-create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      setCreatedCoupons(result.coupons || []);
      setSuccess(`✅ 쿠폰 ${result.created_count}/${result.total_count}개가 성공적으로 생성되었습니다!`);
      setShowResult(true);
      
      // 폼 초기화 (시리즈명 제외)
      setFormData(prev => ({
        ...prev,
        count: 1,
        description: ''
      }));

    } catch (err) {
      console.error('쿠폰 생성 실패:', err);
      setError(err.message || '쿠폰 생성 중 오류가 발생했습니다');
    } finally {
      setIsCreating(false);
    }
  };

  // CSV 다운로드
  const handleDownloadCSV = () => {
    if (!createdCoupons || createdCoupons.length === 0) {
      alert('다운로드할 쿠폰이 없습니다');
      return;
    }

    // CSV 헤더
    const headers = ['쿠폰 코드', '상태', '혜택 기간 (일)', '최대 사용 횟수'];
    
    // CSV 행 데이터
    const rows = createdCoupons.map(coupon => [
      coupon.code,
      coupon.status === 'created' ? '생성됨' : 
      coupon.status === 'already_exists' ? '이미 존재' : '실패',
      coupon.benefit_days || '-',
      coupon.max_uses || '-'
    ]);

    // CSV 문자열 생성
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // BOM 추가 (엑셀에서 한글 깨짐 방지)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // 다운로드 링크 생성
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `coupons_${formData.series_name}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 쿠폰 코드 복사
  const handleCopyAllCodes = () => {
    if (!createdCoupons || createdCoupons.length === 0) {
      alert('복사할 쿠폰이 없습니다');
      return;
    }

    const codes = createdCoupons
      .filter(c => c.status === 'created')
      .map(c => c.code)
      .join('\n');
    
    navigator.clipboard.writeText(codes).then(() => {
      alert('쿠폰 코드가 클립보드에 복사되었습니다!');
    }).catch(err => {
      console.error('복사 실패:', err);
      alert('복사에 실패했습니다');
    });
  };

  // === 사용자 쿠폰 관리 핸들러 ===
  
  // 검색 폼 입력 핸들러
  const handleSearchInputChange = (e) => {
    const { name, value } = e.target;
    setSearchForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 사용자 쿠폰 검색
  const handleSearchUserCoupons = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setSearchError('로그인이 필요합니다');
      return;
    }

    if (!searchForm.email && !searchForm.coupon_code) {
      setSearchError('이메일 또는 쿠폰 코드 중 하나 이상을 입력해주세요');
      return;
    }

    try {
      setIsSearching(true);
      setSearchError(null);
      setSearchSuccess(null);
      setSearchResults([]);

      const token = await user.getIdToken(true);
      
      const requestBody = {};
      if (searchForm.email) requestBody.email = searchForm.email.trim();
      if (searchForm.coupon_code) requestBody.coupon_code = searchForm.coupon_code.trim().toUpperCase();

      const response = await fetch(`${getApiBaseUrl()}/api/coupons/admin/search-user-coupons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      setSearchResults(result.results || []);
      
      if (result.total_count === 0) {
        setSearchSuccess('검색 결과가 없습니다');
      } else {
        setSearchSuccess(`${result.total_count}개의 결과를 찾았습니다`);
      }

    } catch (err) {
      console.error('사용자 쿠폰 검색 실패:', err);
      setSearchError(err.message || '검색 중 오류가 발생했습니다');
    } finally {
      setIsSearching(false);
    }
  };

  // 철회 확인 모달 열기
  const handleOpenRevokeModal = (coupon) => {
    setSelectedCoupon(coupon);
    setShowRevokeModal(true);
  };

  // 철회 확인 모달 닫기
  const handleCloseRevokeModal = () => {
    setSelectedCoupon(null);
    setShowRevokeModal(false);
  };

  // 쿠폰 철회 실행
  const handleRevokeCoupon = async () => {
    if (!selectedCoupon || !user) return;

    try {
      setIsRevoking(true);
      setSearchError(null);

      const token = await user.getIdToken(true);

      const response = await fetch(
        `${getApiBaseUrl()}/api/coupons/admin/revoke-user-coupon/${selectedCoupon.user_coupon_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      // 검색 결과에서 철회된 쿠폰 제거
      setSearchResults(prev => prev.filter(c => c.user_coupon_id !== selectedCoupon.user_coupon_id));
      
      setSearchSuccess(`✅ 쿠폰 철회 완료!\n\n📧 사용자: ${result.user_email}\n🎫 쿠폰: ${result.revoked_coupon_code}\n\n수행된 작업:\n${result.actions_performed.map(a => `• ${a}`).join('\n')}`);
      
      handleCloseRevokeModal();

    } catch (err) {
      console.error('쿠폰 철회 실패:', err);
      setSearchError(err.message || '쿠폰 철회 중 오류가 발생했습니다');
    } finally {
      setIsRevoking(false);
    }
  };

  // === 일괄 삭제 핸들러 ===
  
  const handleToggleCouponSelect = (couponCode) => {
    setSelectedCoupons(prev => {
      if (prev.includes(couponCode)) {
        return prev.filter(code => code !== couponCode);
      } else {
        return [...prev, couponCode];
      }
    });
  };

  const handleSelectAllCoupons = () => {
    if (selectedCoupons.length === masterCoupons.length) {
      setSelectedCoupons([]);
    } else {
      setSelectedCoupons(masterCoupons.map(c => c.code));
    }
  };

  const handleOpenBulkDeleteModal = () => {
    if (selectedCoupons.length === 0) {
      setError('삭제할 쿠폰을 선택해주세요');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const handleCloseBulkDeleteModal = () => {
    setShowBulkDeleteModal(false);
  };

  const handleBulkDeleteCoupons = async () => {
    if (selectedCoupons.length === 0 || !user) return;

    try {
      setIsBulkDeleting(true);
      setError(null);

      const token = await user.getIdToken(true);

      const response = await fetch(
        `${getApiBaseUrl()}/api/coupons/admin/bulk-delete`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ coupon_codes: selectedCoupons })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      setSuccess(`${result.deleted_count}개 쿠폰이 삭제되었습니다.${result.failed_count > 0 ? ` (${result.failed_count}개 실패)` : ''}`);
      setSelectedCoupons([]);
      handleCloseBulkDeleteModal();
      fetchMasterCoupons();

    } catch (err) {
      console.error('쿠폰 일괄 삭제 실패:', err);
      setError(err.message || '쿠폰 일괄 삭제 중 오류가 발생했습니다');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // === 단일 쿠폰 생성 핸들러 ===
  
  const handleSingleCouponInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSingleCouponForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateSingleCoupon = async (e) => {
    e.preventDefault();
    
    if (!user || !singleCouponForm.code || !singleCouponForm.name) {
      setError('필수 항목을 모두 입력해주세요');
      return;
    }

    try {
      setIsCreatingSingle(true);
      setError(null);
      setSuccess(null);

      const token = await user.getIdToken(true);
      
      const requestBody = {
        code: singleCouponForm.code.toUpperCase().trim(),
        name: singleCouponForm.name,
        description: singleCouponForm.description || undefined,
        max_uses: parseInt(singleCouponForm.max_uses),
        valid_until: singleCouponForm.permanent ? null : couponValidUntilIso(singleCouponForm.valid_until),
        coupon_group: (singleCouponForm.coupon_group || '').trim() || null,
        metadata: {
          created_via: 'admin_panel_single',
          created_by_email: user.email
        }
      };
      
      // 혜택 기간 설정 (일 단위 또는 개월 단위)
      if (singleCouponForm.benefit_type === 'days') {
        requestBody.benefit_days = parseInt(singleCouponForm.benefit_days);
      } else {
        requestBody.benefit_months = parseInt(singleCouponForm.benefit_months);
      }

      const response = await fetch(`${getApiBaseUrl()}/api/coupons/admin/create-single`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      setSuccess(`✅ 쿠폰 '${result.coupon.code}'가 성공적으로 생성되었습니다!\n혜택: ${result.coupon.benefit_days}일 PRO\n유효기간: ${result.coupon.permanent ? '영구' : formatDate(result.coupon.valid_until)}`);
      
      // 폼 초기화
      setSingleCouponForm({
        code: '',
        name: '',
        description: '',
        benefit_type: 'months',
        benefit_months: 3,
        benefit_days: 30,
        max_uses: 1,
        permanent: true,
        valid_until: '',
        coupon_group: ''
      });

      // 쿠폰 목록 새로고침
      fetchMasterCoupons();

    } catch (err) {
      console.error('단일 쿠폰 생성 실패:', err);
      setError(err.message || '쿠폰 생성 중 오류가 발생했습니다');
    } finally {
      setIsCreatingSingle(false);
    }
  };

  // === 마스터 쿠폰 목록 핸들러 ===

  const fetchMasterCoupons = async () => {
    if (!user) return;

    try {
      setIsLoadingMasterCoupons(true);
      setMasterCouponsError(null);

      const token = await user.getIdToken(true);

      const response = await fetch(
        `${getApiBaseUrl()}/api/coupons/admin/all?include_inactive=${includeInactive}&limit=10000`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      setMasterCoupons(result.coupons || []);

    } catch (err) {
      console.error('쿠폰 목록 조회 실패:', err);
      setMasterCouponsError(err.message || '쿠폰 목록 조회 중 오류가 발생했습니다');
    } finally {
      setIsLoadingMasterCoupons(false);
    }
  };

  // 페이지 로드 시 쿠폰 목록 조회
  useEffect(() => {
    if (user && hasSuperAdminAccess) {
      fetchMasterCoupons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasSuperAdminAccess, includeInactive]);

  // === 쿠폰 수정 핸들러 ===

  const handleOpenEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setEditForm({
      name: coupon.name || '',
      description: coupon.description || '',
      benefit_days: coupon.benefit_days || 30,
      max_uses: coupon.max_uses || 1,
      active: coupon.active !== false,
      valid_until: couponDateInputValue(coupon.valid_until),
      coupon_group: coupon.coupon_group || ''
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditingCoupon(null);
    setShowEditModal(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateCoupon = async () => {
    if (!editingCoupon || !user) return;

    try {
      setIsUpdating(true);
      setMasterCouponsError(null);

      const token = await user.getIdToken(true);

      const requestBody = {
        name: editForm.name,
        description: editForm.description,
        benefit_days: parseInt(editForm.benefit_days),
        max_uses: parseInt(editForm.max_uses),
        active: editForm.active,
        valid_until: couponValidUntilIso(editForm.valid_until),
        coupon_group: (editForm.coupon_group || '').trim() || null
      };

      const response = await fetch(
        `${getApiBaseUrl()}/api/coupons/admin/${editingCoupon.code}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      setSuccess(`✅ 쿠폰 '${editingCoupon.code}'가 성공적으로 수정되었습니다!`);
      handleCloseEditModal();
      fetchMasterCoupons();

    } catch (err) {
      console.error('쿠폰 수정 실패:', err);
      setMasterCouponsError(err.message || '쿠폰 수정 중 오류가 발생했습니다');
    } finally {
      setIsUpdating(false);
    }
  };

  // === 쿠폰 삭제 핸들러 ===

  const handleOpenDeleteModal = (coupon) => {
    setDeletingCoupon(coupon);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeletingCoupon(null);
    setShowDeleteModal(false);
  };

  const handleDeleteCoupon = async () => {
    if (!deletingCoupon || !user) return;

    try {
      setIsDeleting(true);
      setMasterCouponsError(null);

      const token = await user.getIdToken(true);

      const response = await fetch(
        `${getApiBaseUrl()}/api/coupons/admin/${deletingCoupon.code}/permanent`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      setSuccess(`✅ 쿠폰 '${deletingCoupon.code}'가 영구 삭제되었습니다.\n${result.warning || ''}`);
      handleCloseDeleteModal();
      fetchMasterCoupons();

    } catch (err) {
      console.error('쿠폰 삭제 실패:', err);
      setMasterCouponsError(err.message || '쿠폰 삭제 중 오류가 발생했습니다');
    } finally {
      setIsDeleting(false);
    }
  };

  // 로딩 중
  if (loading || permissionLoading) {
    return (
      <div className="coupon-manager">
        <div className="coupon-manager__loading">
          <div className="coupon-manager__spinner"></div>
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  // 권한 없음
  if (!user || !hasSuperAdminAccess) {
    return (
      <div className="coupon-manager">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>접근 제한 - WhatQuiz</title>
        </Helmet>
        <div className="coupon-manager__error-container">
          <div className="coupon-manager__error-icon">🔒</div>
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지는 관리자만 접근할 수 있습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="coupon-manager">
      <div className="coupon-manager__container">
        {/* 헤더 */}
        <div className="coupon-manager__header">
          <h1>🎫 쿠폰 발행 관리자</h1>
          <p className="coupon-manager__subtitle">
            슈퍼 관리자 전용 쿠폰 일괄 발행 시스템
          </p>
          <div className="coupon-manager__user-badge">
            👤 {user.email}
          </div>
        </div>

        {/* 에러/성공 메시지 */}
        {error && (
          <div className="coupon-manager__alert coupon-manager__alert--error">
            ❌ {error}
          </div>
        )}
        
        {success && (
          <div className="coupon-manager__alert coupon-manager__alert--success">
            {success}
          </div>
        )}

        {/* 쿠폰 생성 폼 */}
        <div className="coupon-manager__form-section">
          <h2 className="coupon-manager__section-title">📝 쿠폰 생성</h2>
          
          {/* 탭 선택 */}
          <div className="coupon-manager__tabs">
            <button
              className={`coupon-manager__tab ${createMode === 'bulk' ? 'coupon-manager__tab--active' : ''}`}
              onClick={() => setCreateMode('bulk')}
            >
              📦 일괄 생성
            </button>
            <button
              className={`coupon-manager__tab ${createMode === 'single' ? 'coupon-manager__tab--active' : ''}`}
              onClick={() => setCreateMode('single')}
            >
              🎫 단일 생성
            </button>
          </div>

          {/* 일괄 생성 폼 */}
          {createMode === 'bulk' && (
            <form onSubmit={handleCreateCoupons} className="coupon-manager__form">
              {/* 시리즈명 */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  쿠폰 시리즈명
                  <span className="coupon-manager__required">*</span>
                </label>
                <input
                  type="text"
                  name="series_name"
                  value={formData.series_name}
                  onChange={handleInputChange}
                  placeholder="예: SAEUM, DAESUNG"
                  required
                  className="coupon-manager__input"
                  disabled={isCreating}
                />
                <p className="coupon-manager__help-text">
                  생성될 쿠폰: {formData.series_name.toUpperCase()}001, {formData.series_name.toUpperCase()}002, ...
                </p>
              </div>

              {/* 혜택 기간 타입 선택 */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  혜택 기간
                  <span className="coupon-manager__required">*</span>
                </label>
                <div className="coupon-manager__benefit-type-toggle">
                  <button
                    type="button"
                    className={`coupon-manager__toggle-btn ${formData.benefit_type === 'months' ? 'coupon-manager__toggle-btn--active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, benefit_type: 'months' }))}
                    disabled={isCreating}
                  >
                    개월 단위
                  </button>
                  <button
                    type="button"
                    className={`coupon-manager__toggle-btn ${formData.benefit_type === 'days' ? 'coupon-manager__toggle-btn--active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, benefit_type: 'days' }))}
                    disabled={isCreating}
                  >
                    일 단위
                  </button>
                </div>
                
                {formData.benefit_type === 'months' ? (
                  <>
                    <input
                      type="number"
                      name="benefit_months"
                      value={formData.benefit_months}
                      onChange={handleInputChange}
                      min="1"
                      max="24"
                      required
                      className="coupon-manager__input"
                      disabled={isCreating}
                      placeholder="1~24 사이 숫자 입력"
                    />
                    <p className="coupon-manager__help-text">
                      {formData.benefit_months}개월 = {formData.benefit_months * 30}일 PRO 이용권
                    </p>
                  </>
                ) : (
                  <>
                    <input
                      type="number"
                      name="benefit_days"
                      value={formData.benefit_days}
                      onChange={handleInputChange}
                      min="1"
                      max="730"
                      required
                      className="coupon-manager__input"
                      disabled={isCreating}
                      placeholder="1~730 사이 숫자 입력"
                    />
                    <p className="coupon-manager__help-text">
                      {formData.benefit_days}일 PRO 이용권
                    </p>
                  </>
                )}
              </div>

              {/* 발행 개수 */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  발행 개수
                  <span className="coupon-manager__required">*</span>
                </label>
                <input
                  type="number"
                  name="count"
                  value={formData.count}
                  onChange={handleInputChange}
                  min="1"
                  max="1000"
                  required
                  className="coupon-manager__input"
                  disabled={isCreating}
                />
                <p className="coupon-manager__help-text">
                  1 ~ 1000개까지 생성 가능
                </p>
              </div>

              {/* 1인당 사용 횟수 */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  쿠폰당 최대 사용 횟수
                </label>
                <input
                  type="number"
                  name="max_uses_per_coupon"
                  value={formData.max_uses_per_coupon}
                  onChange={handleInputChange}
                  min="1"
                  max="100"
                  className="coupon-manager__input"
                  disabled={isCreating}
                />
                <p className="coupon-manager__help-text">
                  각 쿠폰을 몇 명이 사용할 수 있는지 설정
                </p>
              </div>

              {/* 영구 유효 */}
              <div className="coupon-manager__form-group coupon-manager__form-group--checkbox">
                <label className="coupon-manager__checkbox-label">
                  <input
                    type="checkbox"
                    name="permanent"
                    checked={formData.permanent}
                    onChange={handleInputChange}
                    className="coupon-manager__checkbox"
                    disabled={isCreating}
                  />
                  <span>영구 유효 쿠폰</span>
                </label>
                <p className="coupon-manager__help-text">
                  체크 시 만료일 없이 영구적으로 사용 가능
                </p>
              </div>

              {/* 유효기간 (영구가 아닌 경우) */}
              {!formData.permanent && (
                <div className="coupon-manager__form-group">
                  <label className="coupon-manager__label">
                    쿠폰 등록 마감일
                    <span className="coupon-manager__required">*</span>
                  </label>
                  <input
                    type="date"
                    name="valid_until"
                    value={formData.valid_until}
                    onChange={handleInputChange}
                    required={!formData.permanent}
                    className="coupon-manager__input"
                    disabled={isCreating}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="coupon-manager__help-text">
                    이 날짜가 지나면 더 이상 쿠폰 등록 불가
                  </p>
                </div>
              )}

              {/* 설명 (선택) */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  설명 (선택)
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="쿠폰에 대한 설명을 입력하세요"
                  rows="3"
                  className="coupon-manager__textarea"
                  disabled={isCreating}
                />
              </div>

              {/* 쿠폰 그룹 (선택) */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  쿠폰 그룹 (선택)
                </label>
                <input
                  type="text"
                  name="coupon_group"
                  value={formData.coupon_group}
                  onChange={handleInputChange}
                  placeholder="예: ambassador_1st (같은 그룹 1인 1회 제한)"
                  className="coupon-manager__input"
                  disabled={isCreating}
                />
                <p className="coupon-manager__help-text">
                  같은 그룹의 쿠폰은 한 사람이 1개만 사용 가능합니다. 비워두면 제한 없음.
                </p>
              </div>

              {/* 생성 버튼 */}
              <button
                type="submit"
                className="coupon-manager__submit-btn"
                disabled={isCreating || !formData.series_name || !formData.count}
              >
                {isCreating ? (
                  <>
                    <span className="coupon-manager__spinner-small"></span>
                    생성 중...
                  </>
                ) : (
                  <>
                    🚀 쿠폰 {formData.count}개 발행하기
                  </>
                )}
              </button>
            </form>
          )}

          {/* 단일 생성 폼 */}
          {createMode === 'single' && (
            <form onSubmit={handleCreateSingleCoupon} className="coupon-manager__form">
              {/* 쿠폰 코드 */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  쿠폰 코드
                  <span className="coupon-manager__required">*</span>
                </label>
                <input
                  type="text"
                  name="code"
                  value={singleCouponForm.code}
                  onChange={handleSingleCouponInputChange}
                  placeholder="예: NEWYEAR2025, XMAS_EVENT"
                  required
                  className="coupon-manager__input"
                  disabled={isCreatingSingle}
                />
                <p className="coupon-manager__help-text">
                  원하는 코드를 직접 입력 (대문자로 자동 변환됨)
                </p>
              </div>

              {/* 쿠폰명 */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  쿠폰 이름
                  <span className="coupon-manager__required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={singleCouponForm.name}
                  onChange={handleSingleCouponInputChange}
                  placeholder="예: 2025 신년 이벤트 쿠폰"
                  required
                  className="coupon-manager__input"
                  disabled={isCreatingSingle}
                />
              </div>

              {/* 혜택 기간 타입 선택 */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  혜택 기간
                  <span className="coupon-manager__required">*</span>
                </label>
                <div className="coupon-manager__benefit-type-toggle">
                  <button
                    type="button"
                    className={`coupon-manager__toggle-btn ${singleCouponForm.benefit_type === 'months' ? 'coupon-manager__toggle-btn--active' : ''}`}
                    onClick={() => setSingleCouponForm(prev => ({ ...prev, benefit_type: 'months' }))}
                    disabled={isCreatingSingle}
                  >
                    개월 단위
                  </button>
                  <button
                    type="button"
                    className={`coupon-manager__toggle-btn ${singleCouponForm.benefit_type === 'days' ? 'coupon-manager__toggle-btn--active' : ''}`}
                    onClick={() => setSingleCouponForm(prev => ({ ...prev, benefit_type: 'days' }))}
                    disabled={isCreatingSingle}
                  >
                    일 단위
                  </button>
                </div>
                
                {singleCouponForm.benefit_type === 'months' ? (
                  <>
                    <input
                      type="number"
                      name="benefit_months"
                      value={singleCouponForm.benefit_months}
                      onChange={handleSingleCouponInputChange}
                      min="1"
                      max="24"
                      required
                      className="coupon-manager__input"
                      disabled={isCreatingSingle}
                    />
                    <p className="coupon-manager__help-text">
                      {singleCouponForm.benefit_months}개월 = {singleCouponForm.benefit_months * 30}일 PRO 이용권
                    </p>
                  </>
                ) : (
                  <>
                    <input
                      type="number"
                      name="benefit_days"
                      value={singleCouponForm.benefit_days}
                      onChange={handleSingleCouponInputChange}
                      min="1"
                      max="730"
                      required
                      className="coupon-manager__input"
                      disabled={isCreatingSingle}
                    />
                    <p className="coupon-manager__help-text">
                      {singleCouponForm.benefit_days}일 PRO 이용권
                    </p>
                  </>
                )}
              </div>

              {/* 최대 사용 횟수 */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  최대 사용 횟수
                </label>
                <input
                  type="number"
                  name="max_uses"
                  value={singleCouponForm.max_uses}
                  onChange={handleSingleCouponInputChange}
                  min="1"
                  max="10000"
                  className="coupon-manager__input"
                  disabled={isCreatingSingle}
                />
                <p className="coupon-manager__help-text">
                  이벤트 쿠폰의 경우 여러 명이 사용할 수 있게 설정
                </p>
              </div>

              {/* 영구 유효 */}
              <div className="coupon-manager__form-group coupon-manager__form-group--checkbox">
                <label className="coupon-manager__checkbox-label">
                  <input
                    type="checkbox"
                    name="permanent"
                    checked={singleCouponForm.permanent}
                    onChange={handleSingleCouponInputChange}
                    className="coupon-manager__checkbox"
                    disabled={isCreatingSingle}
                  />
                  <span>영구 유효 쿠폰</span>
                </label>
              </div>

              {/* 유효기간 (영구가 아닌 경우) */}
              {!singleCouponForm.permanent && (
                <div className="coupon-manager__form-group">
                  <label className="coupon-manager__label">
                    유효 종료일
                    <span className="coupon-manager__required">*</span>
                  </label>
                  <input
                    type="date"
                    name="valid_until"
                    value={singleCouponForm.valid_until}
                    onChange={handleSingleCouponInputChange}
                    required={!singleCouponForm.permanent}
                    className="coupon-manager__input"
                    disabled={isCreatingSingle}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="coupon-manager__help-text">
                    이 날짜가 지나면 더 이상 쿠폰 등록 불가
                  </p>
                </div>
              )}

              {/* 설명 (선택) */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  설명 (선택)
                </label>
                <textarea
                  name="description"
                  value={singleCouponForm.description}
                  onChange={handleSingleCouponInputChange}
                  placeholder="쿠폰에 대한 설명을 입력하세요"
                  rows="3"
                  className="coupon-manager__textarea"
                  disabled={isCreatingSingle}
                />
              </div>

              {/* 쿠폰 그룹 (선택) */}
              <div className="coupon-manager__form-group">
                <label className="coupon-manager__label">
                  쿠폰 그룹 (선택)
                </label>
                <input
                  type="text"
                  name="coupon_group"
                  value={singleCouponForm.coupon_group}
                  onChange={handleSingleCouponInputChange}
                  placeholder="예: ambassador_1st (같은 그룹 1인 1회 제한)"
                  className="coupon-manager__input"
                  disabled={isCreatingSingle}
                />
                <p className="coupon-manager__help-text">
                  같은 그룹의 쿠폰은 한 사람이 1개만 사용 가능합니다. 비워두면 제한 없음.
                </p>
              </div>

              {/* 생성 버튼 */}
              <button
                type="submit"
                className="coupon-manager__submit-btn coupon-manager__submit-btn--single"
                disabled={isCreatingSingle || !singleCouponForm.code || !singleCouponForm.name}
              >
                {isCreatingSingle ? (
                  <>
                    <span className="coupon-manager__spinner-small"></span>
                    생성 중...
                  </>
                ) : (
                  <>
                    🎫 단일 쿠폰 발행하기
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* 생성 결과 */}
        {showResult && createdCoupons.length > 0 && (
          <div className="coupon-manager__result-section">
            <div className="coupon-manager__result-header">
              <h2 className="coupon-manager__section-title">
                📊 생성 결과
              </h2>
              <div className="coupon-manager__result-actions">
                <button
                  onClick={handleCopyAllCodes}
                  className="coupon-manager__action-btn coupon-manager__action-btn--secondary"
                >
                  📋 코드 복사
                </button>
                <button
                  onClick={handleDownloadCSV}
                  className="coupon-manager__action-btn coupon-manager__action-btn--primary"
                >
                  📥 CSV 다운로드
                </button>
              </div>
            </div>

            <div className="coupon-manager__stats">
              <div className="coupon-manager__stat-item">
                <span className="coupon-manager__stat-label">총 생성</span>
                <span className="coupon-manager__stat-value">{createdCoupons.length}개</span>
              </div>
              <div className="coupon-manager__stat-item coupon-manager__stat-item--success">
                <span className="coupon-manager__stat-label">성공</span>
                <span className="coupon-manager__stat-value">
                  {createdCoupons.filter(c => c.status === 'created').length}개
                </span>
              </div>
              <div className="coupon-manager__stat-item" style={{background: '#e6f7ff', borderLeftColor: '#1890ff'}}>
                <span className="coupon-manager__stat-label">테스트 쿠폰</span>
                <span className="coupon-manager__stat-value">
                  {createdCoupons.filter(c => c.is_test).length}개
                </span>
              </div>
              <div className="coupon-manager__stat-item coupon-manager__stat-item--warning">
                <span className="coupon-manager__stat-label">이미 존재</span>
                <span className="coupon-manager__stat-value">
                  {createdCoupons.filter(c => c.status === 'already_exists').length}개
                </span>
              </div>
              <div className="coupon-manager__stat-item coupon-manager__stat-item--error">
                <span className="coupon-manager__stat-label">실패</span>
                <span className="coupon-manager__stat-value">
                  {createdCoupons.filter(c => c.status === 'failed').length}개
                </span>
              </div>
            </div>

            {/* 쿠폰 목록 */}
            <div className="coupon-manager__coupon-list">
              <div className="coupon-manager__list-header">
                <span className="coupon-manager__list-col coupon-manager__list-col--code">쿠폰 코드</span>
                <span className="coupon-manager__list-col coupon-manager__list-col--status">상태</span>
                <span className="coupon-manager__list-col coupon-manager__list-col--days">혜택 기간</span>
                <span className="coupon-manager__list-col coupon-manager__list-col--uses">최대 사용</span>
              </div>
              
              <div className="coupon-manager__list-body">
                {createdCoupons.map((coupon, index) => (
                  <div 
                    key={index} 
                    className={`coupon-manager__list-row coupon-manager__list-row--${coupon.status} ${coupon.is_test ? 'coupon-manager__list-row--test' : ''}`}
                  >
                    <span className="coupon-manager__list-col coupon-manager__list-col--code">
                      <code>{coupon.code}</code>
                      {coupon.is_test && <span className="coupon-manager__test-badge">🧪 테스트</span>}
                    </span>
                    <span className="coupon-manager__list-col coupon-manager__list-col--status">
                      {coupon.status === 'created' && '✅ 생성됨'}
                      {coupon.status === 'already_exists' && '⚠️ 이미 존재'}
                      {coupon.status === 'failed' && '❌ 실패'}
                    </span>
                    <span className="coupon-manager__list-col coupon-manager__list-col--days">
                      {coupon.benefit_days ? `${coupon.benefit_days}일` : '-'}
                    </span>
                    <span className="coupon-manager__list-col coupon-manager__list-col--uses">
                      {coupon.max_uses ? `${coupon.max_uses}회` : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========== 발행된 쿠폰 목록 섹션 ========== */}
        <div className="coupon-manager__divider"></div>
        
        <div className="coupon-manager__form-section">
          <div className="coupon-manager__section-header">
            <h2 className="coupon-manager__section-title">📋 발행된 쿠폰 목록</h2>
            <div className="coupon-manager__section-actions">
              {selectedCoupons.length > 0 && (
                <button
                  onClick={handleOpenBulkDeleteModal}
                  className="coupon-manager__action-btn coupon-manager__action-btn--danger"
                  disabled={isLoadingMasterCoupons}
                >
                  🗑️ {selectedCoupons.length}개 삭제
                </button>
              )}
              <label className="coupon-manager__checkbox-label coupon-manager__checkbox-label--inline">
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  className="coupon-manager__checkbox"
                />
                <span>비활성 포함</span>
              </label>
              <button
                onClick={fetchMasterCoupons}
                className="coupon-manager__action-btn coupon-manager__action-btn--secondary"
                disabled={isLoadingMasterCoupons}
              >
                🔄 새로고침
              </button>
            </div>
          </div>
          
          {masterCouponsError && (
            <div className="coupon-manager__alert coupon-manager__alert--error">
              ❌ {masterCouponsError}
            </div>
          )}
          
          {isLoadingMasterCoupons ? (
            <div className="coupon-manager__loading-inline">
              <div className="coupon-manager__spinner"></div>
              <p>쿠폰 목록 불러오는 중...</p>
            </div>
          ) : masterCoupons.length > 0 ? (
            <div className="coupon-manager__master-coupon-list">
              <div className="coupon-manager__master-list-header">
                <span className="coupon-manager__master-col coupon-manager__master-col--checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCoupons.length === masterCoupons.length && masterCoupons.length > 0}
                    onChange={handleSelectAllCoupons}
                    className="coupon-manager__checkbox"
                    title="전체 선택"
                  />
                </span>
                <span className="coupon-manager__master-col coupon-manager__master-col--code">코드</span>
                <span className="coupon-manager__master-col coupon-manager__master-col--name">이름</span>
                <span className="coupon-manager__master-col coupon-manager__master-col--days">혜택</span>
                <span className="coupon-manager__master-col coupon-manager__master-col--uses">사용량</span>
                <span className="coupon-manager__master-col coupon-manager__master-col--status">상태</span>
                <span className="coupon-manager__master-col coupon-manager__master-col--valid">유효기간</span>
                <span className="coupon-manager__master-col coupon-manager__master-col--group">그룹</span>
                <span className="coupon-manager__master-col coupon-manager__master-col--actions">관리</span>
              </div>
              
              <div className="coupon-manager__master-list-body">
                {masterCoupons.map((coupon, index) => (
                  <div 
                    key={index} 
                    className={`coupon-manager__master-list-row ${!coupon.active ? 'coupon-manager__master-list-row--inactive' : ''} ${selectedCoupons.includes(coupon.code) ? 'coupon-manager__master-list-row--selected' : ''}`}
                  >
                    <span className="coupon-manager__master-col coupon-manager__master-col--checkbox">
                      <input
                        type="checkbox"
                        checked={selectedCoupons.includes(coupon.code)}
                        onChange={() => handleToggleCouponSelect(coupon.code)}
                        className="coupon-manager__checkbox"
                      />
                    </span>
                    <span className="coupon-manager__master-col coupon-manager__master-col--code">
                      <code>{coupon.code}</code>
                    </span>
                    <span className="coupon-manager__master-col coupon-manager__master-col--name" title={coupon.name}>
                      {coupon.name || '-'}
                    </span>
                    <span className="coupon-manager__master-col coupon-manager__master-col--days">
                      {coupon.benefit_days}일
                    </span>
                    <span className="coupon-manager__master-col coupon-manager__master-col--uses">
                      {coupon.current_uses || 0}/{coupon.max_uses || '∞'}
                    </span>
                    <span className="coupon-manager__master-col coupon-manager__master-col--status">
                      {coupon.active ? (
                        <span className="coupon-manager__status-badge coupon-manager__status-badge--active">활성</span>
                      ) : (
                        <span className="coupon-manager__status-badge coupon-manager__status-badge--inactive">비활성</span>
                      )}
                    </span>
                    <span className="coupon-manager__master-col coupon-manager__master-col--valid">
                      {coupon.valid_until ? new Date(coupon.valid_until).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Asia/Seoul' }) : '영구'}
                    </span>
                    <span className="coupon-manager__master-col coupon-manager__master-col--group">
                      {coupon.coupon_group ? (
                        <span className="coupon-manager__group-badge">{coupon.coupon_group}</span>
                      ) : '-'}
                    </span>
                    <span className="coupon-manager__master-col coupon-manager__master-col--actions">
                      <button
                        onClick={() => handleOpenEditModal(coupon)}
                        className="coupon-manager__icon-btn coupon-manager__icon-btn--edit"
                        title="수정"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleOpenDeleteModal(coupon)}
                        className="coupon-manager__icon-btn coupon-manager__icon-btn--delete"
                        title="삭제"
                      >
                        🗑️
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="coupon-manager__empty-message">발행된 쿠폰이 없습니다.</p>
          )}
          
          <p className="coupon-manager__count-info">
            총 {masterCoupons.length}개의 쿠폰
          </p>
        </div>

        {/* ========== 사용자 쿠폰 관리 섹션 ========== */}
        <div className="coupon-manager__divider"></div>
        
        <div className="coupon-manager__form-section">
          <h2 className="coupon-manager__section-title">🔍 사용자 쿠폰 관리</h2>
          <p className="coupon-manager__section-desc">
            사용자의 쿠폰 등록 내역을 검색하고, 잘못 등록된 쿠폰을 철회할 수 있습니다.
          </p>
          
          {/* 검색 에러/성공 메시지 */}
          {searchError && (
            <div className="coupon-manager__alert coupon-manager__alert--error">
              ❌ {searchError}
            </div>
          )}
          
          {searchSuccess && (
            <div className="coupon-manager__alert coupon-manager__alert--success" style={{ whiteSpace: 'pre-line' }}>
              {searchSuccess}
            </div>
          )}
          
          {/* 검색 폼 */}
          <form onSubmit={handleSearchUserCoupons} className="coupon-manager__form">
            <div className="coupon-manager__form-row">
              <div className="coupon-manager__form-group coupon-manager__form-group--half">
                <label className="coupon-manager__label">
                  사용자 이메일
                </label>
                <input
                  type="email"
                  name="email"
                  value={searchForm.email}
                  onChange={handleSearchInputChange}
                  placeholder="예: yej3424@gmail.com"
                  className="coupon-manager__input"
                  disabled={isSearching}
                />
              </div>
              
              <div className="coupon-manager__form-group coupon-manager__form-group--half">
                <label className="coupon-manager__label">
                  쿠폰 코드
                </label>
                <input
                  type="text"
                  name="coupon_code"
                  value={searchForm.coupon_code}
                  onChange={handleSearchInputChange}
                  placeholder="예: SEMYUNG017"
                  className="coupon-manager__input"
                  disabled={isSearching}
                />
              </div>
            </div>
            
            <p className="coupon-manager__help-text">
              이메일 또는 쿠폰 코드 중 하나 이상을 입력하세요. 둘 다 입력하면 AND 조건으로 검색됩니다.
            </p>
            
            <button
              type="submit"
              className="coupon-manager__submit-btn coupon-manager__submit-btn--search"
              disabled={isSearching || (!searchForm.email && !searchForm.coupon_code)}
            >
              {isSearching ? (
                <>
                  <span className="coupon-manager__spinner-small"></span>
                  검색 중...
                </>
              ) : (
                <>
                  🔍 사용자 쿠폰 검색
                </>
              )}
            </button>
          </form>
        </div>

        {/* 검색 결과 */}
        {searchResults.length > 0 && (
          <div className="coupon-manager__result-section">
            <h2 className="coupon-manager__section-title">
              📋 검색 결과 ({searchResults.length}건)
            </h2>
            
            <div className="coupon-manager__user-coupon-list">
              {searchResults.map((coupon, index) => (
                <div key={index} className="coupon-manager__user-coupon-card">
                  <div className="coupon-manager__user-coupon-header">
                    <div className="coupon-manager__user-coupon-info">
                      <span className="coupon-manager__user-coupon-email">
                        📧 {coupon.user_email}
                      </span>
                      <code className="coupon-manager__user-coupon-code">
                        {coupon.coupon_code}
                      </code>
                      <span className={`coupon-manager__user-coupon-status coupon-manager__user-coupon-status--${coupon.status}`}>
                        {coupon.status === 'used' && '✅ 사용됨'}
                        {coupon.status === 'available' && '🎫 사용 가능'}
                        {coupon.status === 'expired' && '⏰ 만료됨'}
                      </span>
                    </div>
                    <button
                      onClick={() => handleOpenRevokeModal(coupon)}
                      className="coupon-manager__revoke-btn"
                    >
                      🗑️ 철회
                    </button>
                  </div>
                  
                  <div className="coupon-manager__user-coupon-details">
                    <div className="coupon-manager__user-coupon-detail">
                      <span className="coupon-manager__detail-label">쿠폰명</span>
                      <span className="coupon-manager__detail-value">{coupon.coupon_name || '-'}</span>
                    </div>
                    <div className="coupon-manager__user-coupon-detail">
                      <span className="coupon-manager__detail-label">혜택 기간</span>
                      <span className="coupon-manager__detail-value">{coupon.benefit_days ? `${coupon.benefit_days}일` : '-'}</span>
                    </div>
                    <div className="coupon-manager__user-coupon-detail">
                      <span className="coupon-manager__detail-label">등록일</span>
                      <span className="coupon-manager__detail-value">{formatDate(coupon.created_at)}</span>
                    </div>
                    <div className="coupon-manager__user-coupon-detail">
                      <span className="coupon-manager__detail-label">사용일</span>
                      <span className="coupon-manager__detail-value">{formatDate(coupon.used_at)}</span>
                    </div>
                    {coupon.benefit_start_date && (
                      <div className="coupon-manager__user-coupon-detail">
                        <span className="coupon-manager__detail-label">혜택 시작</span>
                        <span className="coupon-manager__detail-value">{formatDate(coupon.benefit_start_date)}</span>
                      </div>
                    )}
                    {coupon.benefit_end_date && (
                      <div className="coupon-manager__user-coupon-detail">
                        <span className="coupon-manager__detail-label">혜택 종료</span>
                        <span className="coupon-manager__detail-value">{formatDate(coupon.benefit_end_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 철회 확인 모달 */}
        {showRevokeModal && selectedCoupon && (
          <div className="coupon-manager__modal-overlay" onClick={handleCloseRevokeModal}>
            <div className="coupon-manager__modal" onClick={(e) => e.stopPropagation()}>
              <div className="coupon-manager__modal-header">
                <h3>⚠️ 쿠폰 철회 확인</h3>
                <button 
                  className="coupon-manager__modal-close"
                  onClick={handleCloseRevokeModal}
                >
                  ✕
                </button>
              </div>
              
              <div className="coupon-manager__modal-body">
                <p className="coupon-manager__modal-warning">
                  정말로 이 쿠폰 등록을 철회하시겠습니까?
                </p>
                
                <div className="coupon-manager__modal-info">
                  <div className="coupon-manager__modal-info-row">
                    <span className="coupon-manager__modal-info-label">사용자</span>
                    <span className="coupon-manager__modal-info-value">{selectedCoupon.user_email}</span>
                  </div>
                  <div className="coupon-manager__modal-info-row">
                    <span className="coupon-manager__modal-info-label">쿠폰 코드</span>
                    <span className="coupon-manager__modal-info-value">{selectedCoupon.coupon_code}</span>
                  </div>
                  <div className="coupon-manager__modal-info-row">
                    <span className="coupon-manager__modal-info-label">상태</span>
                    <span className="coupon-manager__modal-info-value">{selectedCoupon.status}</span>
                  </div>
                </div>
                
                <div className="coupon-manager__modal-actions-info">
                  <p><strong>철회 시 수행되는 작업:</strong></p>
                  <ul>
                    <li>사용자 쿠폰 등록 기록 삭제</li>
                    <li>마스터 쿠폰 사용량 복원 (다시 사용 가능)</li>
                    {selectedCoupon.status === 'used' && (
                      <>
                        <li>사용자 이용권 상태 복원 (PRO → FREE)</li>
                        <li>시스템 통계 업데이트</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
              
              <div className="coupon-manager__modal-footer">
                <button
                  className="coupon-manager__modal-btn coupon-manager__modal-btn--cancel"
                  onClick={handleCloseRevokeModal}
                  disabled={isRevoking}
                >
                  취소
                </button>
                <button
                  className="coupon-manager__modal-btn coupon-manager__modal-btn--confirm"
                  onClick={handleRevokeCoupon}
                  disabled={isRevoking}
                >
                  {isRevoking ? (
                    <>
                      <span className="coupon-manager__spinner-small"></span>
                      처리 중...
                    </>
                  ) : (
                    '철회 확인'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 쿠폰 수정 모달 */}
        {showEditModal && editingCoupon && (
          <div className="coupon-manager__modal-overlay" onClick={handleCloseEditModal}>
            <div className="coupon-manager__modal" onClick={(e) => e.stopPropagation()}>
              <div className="coupon-manager__modal-header">
                <h3>✏️ 쿠폰 수정</h3>
                <button 
                  className="coupon-manager__modal-close"
                  onClick={handleCloseEditModal}
                >
                  ✕
                </button>
              </div>
              
              <div className="coupon-manager__modal-body">
                <div className="coupon-manager__modal-info">
                  <div className="coupon-manager__modal-info-row">
                    <span className="coupon-manager__modal-info-label">쿠폰 코드</span>
                    <span className="coupon-manager__modal-info-value">
                      <code>{editingCoupon.code}</code>
                    </span>
                  </div>
                </div>
                
                <form className="coupon-manager__modal-form">
                  <div className="coupon-manager__form-group">
                    <label className="coupon-manager__label">쿠폰 이름</label>
                    <input
                      type="text"
                      name="name"
                      value={editForm.name}
                      onChange={handleEditFormChange}
                      className="coupon-manager__input"
                      disabled={isUpdating}
                    />
                  </div>
                  
                  <div className="coupon-manager__form-group">
                    <label className="coupon-manager__label">설명</label>
                    <textarea
                      name="description"
                      value={editForm.description}
                      onChange={handleEditFormChange}
                      className="coupon-manager__textarea"
                      rows="2"
                      disabled={isUpdating}
                    />
                  </div>
                  
                  <div className="coupon-manager__form-group">
                    <label className="coupon-manager__label">
                      혜택 기간 (일)
                      <span className="coupon-manager__required">*</span>
                    </label>
                    <input
                      type="number"
                      name="benefit_days"
                      value={editForm.benefit_days}
                      onChange={handleEditFormChange}
                      min="1"
                      max="730"
                      className="coupon-manager__input"
                      disabled={isUpdating}
                    />
                    <p className="coupon-manager__help-text">
                      현재: {editForm.benefit_days}일 PRO 이용권 ({Math.round(editForm.benefit_days / 7)}주)
                    </p>
                  </div>
                  
                  <div className="coupon-manager__form-group">
                    <label className="coupon-manager__label">최대 사용 횟수</label>
                    <input
                      type="number"
                      name="max_uses"
                      value={editForm.max_uses}
                      onChange={handleEditFormChange}
                      min="1"
                      className="coupon-manager__input"
                      disabled={isUpdating}
                    />
                  </div>
                  
                  <div className="coupon-manager__form-group">
                    <label className="coupon-manager__label">유효 종료일</label>
                    <input
                      type="date"
                      name="valid_until"
                      value={editForm.valid_until}
                      onChange={handleEditFormChange}
                      className="coupon-manager__input"
                      disabled={isUpdating}
                    />
                    <p className="coupon-manager__help-text">비워두면 영구 유효</p>
                  </div>

                  <div className="coupon-manager__form-group">
                    <label className="coupon-manager__label">
                      쿠폰 그룹 <span className="coupon-manager__optional">(선택)</span>
                    </label>
                    <input
                      type="text"
                      name="coupon_group"
                      value={editForm.coupon_group}
                      onChange={handleEditFormChange}
                      placeholder="예: ambassador_1st"
                      className="coupon-manager__input"
                      disabled={isUpdating}
                    />
                    <p className="coupon-manager__help-text">같은 그룹 내 쿠폰은 1인 1회만 사용 가능 (비워두면 제한 없음)</p>
                  </div>
                  
                  <div className="coupon-manager__form-group coupon-manager__form-group--checkbox">
                    <label className="coupon-manager__checkbox-label">
                      <input
                        type="checkbox"
                        name="active"
                        checked={editForm.active}
                        onChange={handleEditFormChange}
                        className="coupon-manager__checkbox"
                        disabled={isUpdating}
                      />
                      <span>활성화</span>
                    </label>
                  </div>
                </form>
              </div>
              
              <div className="coupon-manager__modal-footer">
                <button
                  className="coupon-manager__modal-btn coupon-manager__modal-btn--cancel"
                  onClick={handleCloseEditModal}
                  disabled={isUpdating}
                >
                  취소
                </button>
                <button
                  className="coupon-manager__modal-btn coupon-manager__modal-btn--save"
                  onClick={handleUpdateCoupon}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <span className="coupon-manager__spinner-small"></span>
                      저장 중...
                    </>
                  ) : (
                    '저장'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 쿠폰 일괄 삭제 확인 모달 */}
        {showBulkDeleteModal && (
          <div className="coupon-manager__modal-overlay" onClick={handleCloseBulkDeleteModal}>
            <div className="coupon-manager__modal" onClick={(e) => e.stopPropagation()}>
              <div className="coupon-manager__modal-header">
                <h3>🗑️ 쿠폰 일괄 삭제 확인</h3>
                <button 
                  className="coupon-manager__modal-close"
                  onClick={handleCloseBulkDeleteModal}
                >
                  ✕
                </button>
              </div>
              
              <div className="coupon-manager__modal-body">
                <p className="coupon-manager__modal-warning coupon-manager__modal-warning--danger">
                  정말로 <strong>{selectedCoupons.length}개</strong>의 쿠폰을 <strong>영구 삭제</strong>하시겠습니까?
                </p>
                <p className="coupon-manager__modal-warning-sub">
                  이 작업은 되돌릴 수 없습니다!
                </p>
                
                <div className="coupon-manager__modal-info">
                  <div className="coupon-manager__modal-info-row">
                    <span className="coupon-manager__modal-info-label">삭제 대상</span>
                    <span className="coupon-manager__modal-info-value">{selectedCoupons.length}개 쿠폰</span>
                  </div>
                  <div className="coupon-manager__bulk-delete-list">
                    {selectedCoupons.slice(0, 10).map(code => (
                      <code key={code} className="coupon-manager__bulk-delete-code">{code}</code>
                    ))}
                    {selectedCoupons.length > 10 && (
                      <span className="coupon-manager__bulk-delete-more">...외 {selectedCoupons.length - 10}개</span>
                    )}
                  </div>
                </div>
                
                <div className="coupon-manager__modal-actions-info coupon-manager__modal-actions-info--info">
                  <p>이미 등록/사용한 사용자의 혜택은 유지됩니다.</p>
                  <p>삭제 시 새로운 등록만 불가능해집니다.</p>
                </div>
              </div>
              
              <div className="coupon-manager__modal-footer">
                <button
                  className="coupon-manager__modal-btn coupon-manager__modal-btn--cancel"
                  onClick={handleCloseBulkDeleteModal}
                  disabled={isBulkDeleting}
                >
                  취소
                </button>
                <button
                  className="coupon-manager__modal-btn coupon-manager__modal-btn--danger"
                  onClick={handleBulkDeleteCoupons}
                  disabled={isBulkDeleting}
                >
                  {isBulkDeleting ? (
                    <>
                      <span className="coupon-manager__spinner-small"></span>
                      삭제 중...
                    </>
                  ) : (
                    `${selectedCoupons.length}개 영구 삭제`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 쿠폰 삭제 확인 모달 */}
        {showDeleteModal && deletingCoupon && (
          <div className="coupon-manager__modal-overlay" onClick={handleCloseDeleteModal}>
            <div className="coupon-manager__modal" onClick={(e) => e.stopPropagation()}>
              <div className="coupon-manager__modal-header">
                <h3>🗑️ 쿠폰 삭제 확인</h3>
                <button 
                  className="coupon-manager__modal-close"
                  onClick={handleCloseDeleteModal}
                >
                  ✕
                </button>
              </div>
              
              <div className="coupon-manager__modal-body">
                <p className="coupon-manager__modal-warning coupon-manager__modal-warning--danger">
                  ⚠️ 정말로 이 쿠폰을 <strong>영구 삭제</strong>하시겠습니까?
                </p>
                <p className="coupon-manager__modal-warning-sub">
                  이 작업은 되돌릴 수 없습니다!
                </p>
                
                <div className="coupon-manager__modal-info">
                  <div className="coupon-manager__modal-info-row">
                    <span className="coupon-manager__modal-info-label">쿠폰 코드</span>
                    <span className="coupon-manager__modal-info-value">
                      <code>{deletingCoupon.code}</code>
                    </span>
                  </div>
                  <div className="coupon-manager__modal-info-row">
                    <span className="coupon-manager__modal-info-label">쿠폰명</span>
                    <span className="coupon-manager__modal-info-value">{deletingCoupon.name}</span>
                  </div>
                  <div className="coupon-manager__modal-info-row">
                    <span className="coupon-manager__modal-info-label">현재 사용량</span>
                    <span className="coupon-manager__modal-info-value">
                      {deletingCoupon.current_uses || 0}회 사용됨
                    </span>
                  </div>
                </div>
                
                {(deletingCoupon.current_uses || 0) > 0 && (
                  <div className="coupon-manager__modal-actions-info coupon-manager__modal-actions-info--info">
                    <p>ℹ️ <strong>참고:</strong> 이미 등록/사용한 사용자의 혜택은 유지됩니다.</p>
                    <p>삭제 시 새로운 등록만 불가능해집니다.</p>
                  </div>
                )}
              </div>
              
              <div className="coupon-manager__modal-footer">
                <button
                  className="coupon-manager__modal-btn coupon-manager__modal-btn--cancel"
                  onClick={handleCloseDeleteModal}
                  disabled={isDeleting}
                >
                  취소
                </button>
                <button
                  className="coupon-manager__modal-btn coupon-manager__modal-btn--danger"
                  onClick={handleDeleteCoupon}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="coupon-manager__spinner-small"></span>
                      삭제 중...
                    </>
                  ) : (
                    '영구 삭제'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponManager;

